import {
  BATCH_UPLOAD_SUCCESS,
  BATCH_UPLOAD_FAILURE,
  BATCH_UPLOAD_EDIT_LOAD_BATCH,
  BATCH_UPLOAD_EDIT_ABORT_BATCH,
  BATCH_UPLOAD_EDIT_UPDATE_ROW,
  BATCH_UPLOAD_EDIT_TOGGLE_SKIP_ROW,
  BATCH_UPLOAD_EDIT_SET_LOADING,
  BATCH_UPLOAD_EDIT_SET_LOADING_PROGRESS
} from '@constants/action-types';
import axios from 'axios';
import { discardChanges } from '@actions/confirmation-actions';
import { downloadCSVFromURL } from '@actions/data-table-actions';
import { getServerErrorAction } from '@actions/shared-actions';
import { BASE_API_URL, getAPIRequestUrl } from '@constants/endpoints';
import { fetchMissingEntityFields, geocodeBatchRows } from '@utils/batch-loading-utils';
import { buildConfirmableAction } from '@utils/confirmation-utils';
import {
  getAction, getTemplate, mergeActionWithTemplate, computeMetadataDefaults
} from '@utils/data-detail-utils';
import { getConfigDetails, getEntityTypeCategoryTypes } from '@components/config/selectors';

const uploadSuccess = payload => ({
  type: BATCH_UPLOAD_SUCCESS,
  payload
});

const uploadError = error => ({
  type: BATCH_UPLOAD_FAILURE,
  payload: error
});

export const uploadBatch = (upload, onProgress, onFail) => {
  const url = getAPIRequestUrl('batch');
  const formData = new FormData();
  formData.append('file', upload.file);
  formData.append('active', upload.active);
  formData.append('status', upload.status);
  if (upload.layerType) {
    formData.append('layerType', upload.layerType);
    formData.append('type', 2);
  } else
    if (upload.entityType) {
      formData.append('entityType', upload.entityType);
      formData.append('type', 3);
    }
  // The batch file format version (v2 is the batch edit page JSON data, see convertBatchToJSONFile):
  if (upload.version) {
    formData.append('version', upload.version);
  }
  const config = { onUploadProgress: onProgress };
  const request = axios.post(url, formData, config);
  return dispatch =>
    request.then(
      data => dispatch(uploadSuccess(data)),
      error => {
        if (onFail) {
          onFail(error);
        }
        return dispatch(uploadError(error));
      }
    );
};

// Download a CSV template for the specified entity type:
export const downloadTemplate = type => {
  const url = `${BASE_API_URL}/batch/template/${type}/`;
  const filename = `${type}-template.csv`;
  return dispatch => dispatch(downloadCSVFromURL(url, filename, type));
};

export const validateBatch = (file, type, onValidationResults) => {
  const url = `${BASE_API_URL}/batch/validate/${type}/`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  const request = axios.post(url, formData);
  request.then(
    data => {
      onValidationResults(data?.data);
    },
    error => {
      onValidationResults({
        errors: [
          `Validation failed: ${error}`
        ]
      });
    }
  );
};

export const getSchema = (file, type, onError, onSchemaResults) => {
  const url = `${BASE_API_URL}/batch/schema/${type}/`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  const request = axios.post(url, formData);
  request.then(
    data => {
      onSchemaResults(data?.data?.rows);
    },
    error => {
      onError(`Error while retrieving fields: ${error}`);
    }
  );
};

export const convertToJSON = (file, type, fields, onError, onResults) => {
  const url = `${BASE_API_URL}/batch/tojson/${type}/`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fields', fields);
  formData.append('type', type);
  const request = axios.post(url, formData);
  request.then(
    data => {
      onResults(data?.data);
    },
    error => {
      onError(`Error while retrieving fields: ${error}`);
    }
  );
};

export const loadBatch = ({ fileId, rows, fileName, entityType }) => ({
  type: BATCH_UPLOAD_EDIT_LOAD_BATCH,
  payload: { fileId, rows, fileName, entityType }
});

// Remove a loaded batch (and its edits) from the store.
const removeBatch = fileId => ({
  type: BATCH_UPLOAD_EDIT_ABORT_BATCH,
  payload: { fileId }
});

const BATCH_ABORTED_MESSAGE = 'The batch data could not be loaded. Please upload the file again.';

// Remove a loaded batch from the store (i.e. if its data loading can't be run),
// letting the user know with an error message.
export const abortBatch = fileId => dispatch => {
  dispatch(removeBatch(fileId));
  dispatch(getServerErrorAction('batch', new Error(BATCH_ABORTED_MESSAGE)));
};

// Discard the batch changes when navigating away from the batch edit page: the batch
// is removed, so it's loaded again from the file when returning to the page.
const discardBatchChanges = (fileId, location) => dispatch => {
  dispatch(removeBatch(fileId));
  return dispatch(discardChanges(location));
};

export const getConfirmableDiscardBatchChanges = buildConfirmableAction(
  discardBatchChanges,
  'batchActionsDiscardBatchChanges'
);

export const updateRow = ({ fileId, rowId, updates, errors, segmentErrors }) => ({
  type: BATCH_UPLOAD_EDIT_UPDATE_ROW,
  payload: { fileId, rowId, updates, errors, segmentErrors }
});

export const toggleSkipRow = ({ fileId, rowId }) => ({
  type: BATCH_UPLOAD_EDIT_TOGGLE_SKIP_ROW,
  payload: { fileId, rowId }
});

export const setBatchLoading = (fileId, loading) => ({
  type: BATCH_UPLOAD_EDIT_SET_LOADING,
  payload: { fileId, loading }
});

export const setBatchLoadingProgress = (fileId, { geocoding, entityFields, total }) => ({
  type: BATCH_UPLOAD_EDIT_SET_LOADING_PROGRESS,
  payload: { fileId, geocoding, entityFields, total }
});

// Return the entity metadata from the entity type OPTIONS response (as stored by
// fetchDataDetailOptions), merged with the form template like the entity detail
// page does (see fetchDataDetail), or null if there are no options.
const getEntityMetadata = (state, entityType, options) => {
  if (!options) {
    return null;
  }
  let { action } = getAction(options);
  const template = getTemplate(entityType, getConfigDetails(state));
  if (template) {
    action = computeMetadataDefaults(
      mergeActionWithTemplate(action, template),
      state.dataTypes
    );
  }
  return action;
};

// Coordinated loading that fetches the existing entity data and geocodes the locations
// of the batch upload rows, updating the rows and the loading progress as they complete.
// For each row, the entity data update is always applied before the geocoding one.
//
// options is the entity type OPTIONS response (state.dataDetail.options, which the caller
// must make sure was fetched for entityType), used for the entity data metadata.
// geocodingFields is only set for v2 batch uploads (see geocodeBatchRows).
export const runBatchDataLoading = (
  fileId, rows, entityType, entityConfig, options, geocodingFields = null
) => async (dispatch, getState) => {
  dispatch(setBatchLoading(fileId, true));

  let fetchPromises;
  let geocodePromises;
  try {
    const state = getState();
    // Layer geocoding must be enabled globally (USE_LAYER_GEOCODING setting) and for the entity type:
    const useLayerGeocoding = Boolean(state.config?.USE_LAYER_GEOCODING && entityConfig?.use_layer_geocoding);

    fetchPromises = fetchMissingEntityFields(
      rows, entityType, getEntityMetadata(state, entityType, options), getEntityTypeCategoryTypes(state, entityType)
    );
    geocodePromises = geocodeBatchRows(rows, geocodingFields, useLayerGeocoding);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error starting the batch data loading (file: ${fileId}):`, error);
    // The entity data can't be loaded, so remove the batch instead of leaving it
    // with only the CSV data:
    dispatch(abortBatch(fileId));
    return;
  }

  // Set initial progress
  const totalWork = Object.keys(fetchPromises).length + Object.keys(geocodePromises).length;
  dispatch(setBatchLoadingProgress(fileId, { geocoding: 0, entityFields: 0, total: totalWork }));

  // Progress is counted as soon as each promise is fulfilled, independently of the row updates:
  // (failed promises also count as complete):
  let entityFieldsCount = 0;
  let geocodingCount = 0;
  Object.values(fetchPromises).forEach(async promise => {
    try {
      await promise;
    } catch {
      // Ignored
    }
    entityFieldsCount += 1;
    dispatch(setBatchLoadingProgress(fileId, { entityFields: entityFieldsCount }));
  });
  Object.values(geocodePromises).forEach(async promise => {
    try {
      await promise;
    } catch {
      // Ignored
    }
    geocodingCount += 1;
    dispatch(setBatchLoadingProgress(fileId, { geocoding: geocodingCount }));
  });

  // Both run in parallel, but each row's geocoding update waits for its entity data update
  // (if either fails, the row's remaining updates are skipped):
  rows.forEach(async row => {
    const fetchPromise = fetchPromises[row.id];
    const geocodePromise = geocodePromises[row.id];
    try {
      if (fetchPromise) {
        const result = await fetchPromise;
        if (result) {
          dispatch(updateRow({ fileId, ...result }));
        }
      }
      if (geocodePromise) {
        dispatch(updateRow({ fileId, ...(await geocodePromise) }));
      }
    } catch {
      // Ignored
    }
  });

  // Clear loading state once every fetch and geocode promise has finished, whether fulfilled or rejected:
  await Promise.allSettled([...Object.values(fetchPromises), ...Object.values(geocodePromises)]);
  dispatch(setBatchLoading(fileId, false));
};
